import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/api';

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  const [newAccount, setNewAccount] = useState({
    label: '',
    privateKey: '',
    keyId: '',
    issuerId: ''
  });

  useEffect(() => {
    // Load saved accounts from localStorage
    const accounts = localStorage.getItem('appstoreAccounts');
    if (accounts) {
      try {
        const parsed = JSON.parse(accounts);
        setSavedAccounts(parsed);
      } catch (err) {
        console.error('Failed to parse saved accounts:', err);
      }
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setNewAccount({ ...newAccount, privateKey: event.target.result });
    };
    reader.readAsText(file);
  };

  const saveAccount = () => {
    if (!newAccount.label || !newAccount.privateKey || !newAccount.keyId || !newAccount.issuerId) {
      alert('Please fill in all fields');
      return;
    }

    const accounts = [...savedAccounts, { ...newAccount, id: Date.now().toString() }];
    setSavedAccounts(accounts);
    localStorage.setItem('appstoreAccounts', JSON.stringify(accounts));
    
    setNewAccount({
      label: '',
      privateKey: '',
      keyId: '',
      issuerId: ''
    });
    setShowAddAccount(false);
    alert('Account saved successfully!');
  };

  const deleteAccount = (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    
    const accounts = savedAccounts.filter(acc => acc.id !== accountId);
    setSavedAccounts(accounts);
    localStorage.setItem('appstoreAccounts', JSON.stringify(accounts));
    
    if (selectedAccount?.id === accountId) {
      setSelectedAccount(null);
    }
  };

  const handleLogin = async (credentials) => {
    setError('');
    setLoading(true);

    try {
      const response = await login(credentials);
      if (response.data.success) {
        onLogin();
        navigate('/');
      } else {
        const errorMsg = typeof response.data.error === 'object' 
          ? response.data.error.message || 'Login failed'
          : response.data.error || 'Login failed';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to connect to server'
        : err.response?.data?.error || err.message || 'Failed to connect to server';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const loginWithAccount = (account) => {
    handleLogin({
      privateKey: account.privateKey,
      keyId: account.keyId,
      issuerId: account.issuerId
    });
  };

  const loginWithNewAccount = () => {
    handleLogin({
      privateKey: newAccount.privateKey,
      keyId: newAccount.keyId,
      issuerId: newAccount.issuerId
    });
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">🍎</div>
          <h1 className="login-title">App Store Connect</h1>
          <p className="login-subtitle">Sign in to manage your apps</p>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {!showAddAccount ? (
          <>
            {/* Saved Accounts */}
            {savedAccounts.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <label className="form-label">Your Accounts</label>
                {savedAccounts.map(account => (
                  <div 
                    key={account.id}
                    className={`saved-account ${selectedAccount?.id === account.id ? 'selected' : ''}`}
                    onClick={() => setSelectedAccount(account)}
                  >
                    <div className="saved-account-icon">👤</div>
                    <div className="saved-account-info">
                      <div className="saved-account-name">{account.label}</div>
                      <div className="saved-account-id">
                        {account.issuerId.substring(0, 20)}...
                      </div>
                    </div>
                    <div className="saved-account-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          loginWithAccount(account);
                        }}
                        disabled={loading}
                      >
                        {loading && selectedAccount?.id === account.id ? 'Signing in...' : 'Sign In'}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAccount(account.id);
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {savedAccounts.length > 0 && (
              <div className="login-divider"><span>or</span></div>
            )}

            {/* Add New Account Button */}
            <button
              className="btn btn-secondary btn-block"
              onClick={() => setShowAddAccount(true)}
            >
              ➕ Add New Account
            </button>
          </>
        ) : (
          <>
            {/* Add Account Form */}
            <div className="section-header" style={{ marginBottom: '24px' }}>
              <h3 className="section-title">Add New Account</h3>
            </div>

            <div className="form-group">
              <label className="form-label">Account Label</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., My Company Account"
                value={newAccount.label}
                onChange={(e) => setNewAccount({ ...newAccount, label: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Issuer ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={newAccount.issuerId}
                onChange={(e) => setNewAccount({ ...newAccount, issuerId: e.target.value })}
              />
              <p className="form-hint">
                UUID format from App Store Connect → Users and Access → Keys
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Key ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="XXXXXXXXXX"
                maxLength={10}
                value={newAccount.keyId}
                onChange={(e) => setNewAccount({ ...newAccount, keyId: e.target.value })}
              />
              <p className="form-hint">
                10-character identifier from App Store Connect
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Private Key (.p8 file)</label>
              <div 
                className={`screenshot-upload ${newAccount.privateKey ? 'uploaded' : ''}`}
                onClick={() => document.getElementById('p8-upload').click()}
                style={{ padding: '24px' }}
              >
                <input
                  id="p8-upload"
                  type="file"
                  accept=".p8"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <div className="screenshot-upload-icon">📄</div>
                {newAccount.privateKey ? (
                  <>
                    <p style={{ color: 'var(--success)', fontWeight: 600 }}>✓ File uploaded successfully</p>
                    <p className="screenshot-upload-text">Click to change file</p>
                  </>
                ) : (
                  <>
                    <p>Click to upload .p8 file</p>
                    <p className="screenshot-upload-text">
                      Or drag and drop your AuthKey file here
                    </p>
                  </>
                )}
              </div>
              <p className="form-hint">
                Download from App Store Connect → Users and Access → Keys
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn btn-ghost"
                style={{ flex: 1 }}
                onClick={() => {
                  setShowAddAccount(false);
                  setNewAccount({
                    label: '',
                    privateKey: '',
                    keyId: '',
                    issuerId: ''
                  });
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={saveAccount}
              >
                Save Account
              </button>
            </div>

            <div className="login-divider"><span>or</span></div>

            {/* Option to Sign In Without Saving */}
            <button
              className="btn btn-secondary btn-block"
              onClick={loginWithNewAccount}
              disabled={loading || !newAccount.privateKey || !newAccount.keyId || !newAccount.issuerId}
            >
              {loading ? 'Signing in...' : 'Sign In Without Saving'}
            </button>
            <p className="form-hint" style={{ textAlign: 'center', marginTop: '8px' }}>
              Use this account once without saving it
            </p>
          </>
        )}

        <div className="alert alert-warning" style={{ marginTop: '24px' }}>
          <span className="alert-icon">🔒</span>
          <span>
            <strong>Security Note:</strong> Your credentials are stored locally and never sent to external servers except App Store Connect API.
          </span>
        </div>
      </div>
    </div>
  );
}

export default Login;
